import * as Sentry from '@sentry/react';
import { usePusherContext } from 'contexts/PusherContext';
import { PusherEvent } from 'pusher-js/types/src/core/connection/protocol/message-types';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { RootState, useDispatch, useSelector } from 'store';
import { fetchPartData, partDataSelector } from 'store/slices/apiV1/partData';
import {
  createOrUpdatePartsStatus,
  getPartStatusSelector,
} from 'store/slices/apiV1/partStatus';
import {
  Part,
  PartAlts,
  PartCore,
  PartMarket,
  PartSpecs,
  PartSuggestions,
  PartSupply,
} from 'types/part';
import {
  LoadingStatusTypes,
  PartRequestStateType,
  PartSchemas,
  partStatusRequestTypes,
} from 'types/partStatus';

type PartDataRequesterContextType = {
  getIsPartLoading: ({
    partId,
    schema,
  }: {
    partId: Part['id'];
    schema: PartSchemas;
  }) => boolean;
  getPartDataBySchema: ({
    partId,
    schema,
  }: {
    partId: Part['id'];
    schema: PartSchemas;
  }) =>
    | PartCore
    | PartSupply
    | PartAlts
    | PartSpecs
    | PartMarket
    | PartSuggestions;
};

export const PartDataRequesterContext = createContext(
  {} as PartDataRequesterContextType
);

function filterReadyStatusBySchema(
  partStatus: Record<string, PartRequestStateType>,
  schema: PartSchemas
) {
  return Object.keys(partStatus).filter(
    (partId) =>
      (partStatus[partId][schema] as partStatusRequestTypes) ===
      partStatusRequestTypes.READY
  );
}

function filterRetryNowStatusBySchema(
  partStatus: Record<string, PartRequestStateType>,
  schema: PartSchemas
) {
  return Object.keys(partStatus).filter(
    (partId) =>
      (partStatus[partId][schema] as partStatusRequestTypes) ===
      partStatusRequestTypes.READY_RETRY_NOW
  );
}

const PartDataRequesterProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { authToken } = useSelector((state: RootState) => state.session);
  const batchTimeout = 500;
  const retryBatchTimeout = 1000 * 5;
  const maxBatchSize = 100;

  const partStatus = useSelector((state: RootState) =>
    getPartStatusSelector(state)
  );
  const partData = useSelector((state: RootState) => partDataSelector(state));
  const { pusherChannel } = usePusherContext();

  const handlePusherMessage = useCallback(
    async (
      __: PusherEvent,
      eventData: { id: string; status: string; message: string; schema: string }
    ) => {
      // early return if the user is not logged in
      if (!authToken) return;
      try {
        if (eventData?.id && eventData?.schema) {
          const schema = eventData.schema as PartSchemas;
          if (Object.values(PartSchemas).includes(schema)) {
            // check if this part/schema combination has already been fetched in partData and only refetch if it has already been fetched
            const partAlreadyFetched =
              partStatus[eventData.id]?.[schema] ===
              partStatusRequestTypes.SUCCESS;
            if (partAlreadyFetched) {
              dispatch(
                createOrUpdatePartsStatus({
                  partIds: [eventData.id],
                  schemas: [schema],
                  statusType: partStatusRequestTypes.READY,
                })
              );
            }
          }
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [dispatch, partStatus]
  );

  const bindChannel = useCallback(() => {
    pusherChannel.bind_global(handlePusherMessage);
  }, [pusherChannel, handlePusherMessage]);

  const unbindChannel = useCallback(() => {
    pusherChannel.unbind_global(handlePusherMessage);
  }, [pusherChannel, handlePusherMessage]);

  useEffect(() => {
    bindChannel();
    return () => {
      unbindChannel();
    };
  }, [bindChannel, unbindChannel]);

  const partsCoreDataReady = useMemo(
    () => filterReadyStatusBySchema(partStatus, PartSchemas.core),
    [partStatus]
  );
  const partsSupplyDataReady = useMemo(
    () => filterReadyStatusBySchema(partStatus, PartSchemas.supply),
    [partStatus]
  );
  const partsSupplyRetryDataReady = useMemo(
    () => filterRetryNowStatusBySchema(partStatus, PartSchemas.supply),
    [partStatus]
  );
  const partsAltsDataReady = useMemo(
    () => filterReadyStatusBySchema(partStatus, PartSchemas.alts),
    [partStatus]
  );
  const partsSpecsDataReady = useMemo(
    () => filterReadyStatusBySchema(partStatus, PartSchemas.specs),
    [partStatus]
  );
  const partsMarketDataReady = useMemo(
    () => filterReadyStatusBySchema(partStatus, PartSchemas.market),
    [partStatus]
  );
  const partsSuggestionsDataReady = useMemo(
    () => filterReadyStatusBySchema(partStatus, PartSchemas.suggestions),
    [partStatus]
  );

  useEffect(() => {
    if (!authToken) {
      return undefined;
    }
    const timer = setTimeout(() => {
      const schemas: PartSchemas[] = [];
      const parts = new Set<Part['id']>();
      if (partsCoreDataReady?.length > 0) {
        schemas.push(PartSchemas.core);
        for (const partId of partsCoreDataReady) {
          parts.add(partId);
        }
      }
      if (partsSupplyDataReady?.length > 0) {
        schemas.push(PartSchemas.supply);
        for (const partId of partsSupplyDataReady) {
          parts.add(partId);
        }
      }
      if (partsSupplyRetryDataReady?.length > 0) {
        schemas.push(PartSchemas.supply);
        for (const partId of partsSupplyRetryDataReady) {
          parts.add(partId);
        }
      }
      if (partsAltsDataReady?.length > 0) {
        schemas.push(PartSchemas.alts);
        for (const partId of partsAltsDataReady) {
          parts.add(partId);
        }
      }
      if (partsSpecsDataReady?.length > 0) {
        schemas.push(PartSchemas.specs);
        for (const partId of partsSpecsDataReady) {
          parts.add(partId);
        }
      }
      if (partsMarketDataReady?.length > 0) {
        schemas.push(PartSchemas.market);
        for (const partId of partsMarketDataReady) {
          parts.add(partId);
        }
      }
      if (partsSuggestionsDataReady?.length > 0) {
        schemas.push(PartSchemas.suggestions);
        for (const partId of partsSuggestionsDataReady) {
          parts.add(partId);
        }
      }
      if (schemas.length > 0 && parts.size > 0) {
        // This makes the simplifying assumption that all parts requested within the batchTimeout need the same schema
        // data. This might result in fetching more than required in some edge cases, but doesn't increase the net number
        // of requests to Flagship/KB.
        dispatch(
          fetchPartData({
            schemas,
            parts: Array.from(parts).slice(0, maxBatchSize),
          })
        );
      }
    }, batchTimeout);
    return () => clearTimeout(timer);
  }, [
    partsCoreDataReady,
    partsSupplyDataReady,
    partsSupplyRetryDataReady,
    partsAltsDataReady,
    partsSpecsDataReady,
    partsMarketDataReady,
    partsSuggestionsDataReady,
    dispatch,
    authToken,
  ]);

  const getIsPartLoading = ({
    partId,
    schema,
  }: {
    partId: Part['id'];
    schema: PartSchemas;
  }) =>
    LoadingStatusTypes.includes(
      partStatus?.[partId]?.[schema] as partStatusRequestTypes
    );

  const getPartDataBySchema = ({
    partId,
    schema,
  }: {
    partId: Part['id'];
    schema: PartSchemas;
  }) => partData[schema].byId?.[partId];

  return (
    <PartDataRequesterContext.Provider
      value={{
        getIsPartLoading,
        getPartDataBySchema,
      }}
    >
      {children}
    </PartDataRequesterContext.Provider>
  );
};

export default PartDataRequesterProvider;
